import { oids } from "@/configs/Settings";
import { endpointService } from "../../messaging/services/endpointService";
import { crmContractService } from "../../contracts/services/crmContractService";
import type {
  CrmSetupConfigurationStatus,
  CrmSetupMetaConnectionResult,
} from "../types";
import {
  fetchWabaPhoneNumbers,
  pickPhoneContacts,
  pickPrimaryDisplayPhoneNumber,
} from "./whatsappBusinessSetupService";
import { fhirClients } from "src-next/shared/api/fhirClient";

interface CrmSetupConfigurationContext {
  organizationId?: string;
}

const PHONE_NUMBER_ID_EXTENSION_URL = "https://fluxmed.com.br/fhir/StructureDefinition/whatsapp-phone-number-id";

function extractPrimaryPhoneNumberId(
  endpoint: (fhir5.Endpoint & { id: string }) | null,
): string | null {
  if (!endpoint?.contact?.length) return null;
  const firstContact = endpoint.contact[0];
  const phoneIdExtension = firstContact.extension?.find(
    (extension) => extension.url === PHONE_NUMBER_ID_EXTENSION_URL,
  );
  const valueString = (phoneIdExtension as { valueString?: string } | undefined)?.valueString;
  return valueString?.trim() || null;
}

function prioritizePhoneContacts(
  phoneContacts: ReturnType<typeof pickPhoneContacts>,
  selectedPrimaryPhoneNumberId: string | null,
): ReturnType<typeof pickPhoneContacts> {
  if (!selectedPrimaryPhoneNumberId) return phoneContacts;
  const selectedContact = phoneContacts.find(
    (phoneContact) => phoneContact.id === selectedPrimaryPhoneNumberId,
  );
  if (!selectedContact) return phoneContacts;
  return [
    selectedContact,
    ...phoneContacts.filter((phoneContact) => phoneContact.id !== selectedContact.id),
  ];
}

function buildStatusFromReality(params: {
  endpoint: (fhir5.Endpoint & { id: string }) | null;
  endpointWabaId: string | null;
  hasCrmContract: boolean;
}): CrmSetupConfigurationStatus {
  const { endpoint, endpointWabaId, hasCrmContract } = params;

  if (endpoint?.status === "active") {
    return {
      selectedOption: "metaEmbeddedSignup",
      metaConnection: {
        authorizationCode: null,
        businessId: null,
        wabaName: endpoint.name ?? null,
        wabaId: endpointWabaId,
        primaryPhoneNumberId: extractPrimaryPhoneNumberId(endpoint),
        primaryPhoneValue: endpoint.contact?.[0]?.value ?? null,
      },
    };
  }

  if (hasCrmContract) {
    return {
      selectedOption: "fluxmedDefault",
      metaConnection: null,
    };
  }

  return {
    selectedOption: null,
    metaConnection: null,
  };
}

async function getStatus(
  context?: CrmSetupConfigurationContext,
): Promise<CrmSetupConfigurationStatus> {
  const organizationId = context?.organizationId;

  const [endpoint, endpointWabaId, hasCrmContract] = await Promise.all([
    endpointService.getActiveWhatsAppEndpointByContext({ organizationId }),
    endpointService.getWabaIdRank1ByContext({ organizationId }),
    crmContractService.hasActiveCrmContractByOrganization({ organizationId }),
  ]);

  return buildStatusFromReality({ endpoint, endpointWabaId, hasCrmContract });
}

async function persistMetaEndpoint(
  metaConnection: CrmSetupMetaConnectionResult,
  context?: CrmSetupConfigurationContext,
): Promise<void> {
  const wabaId = metaConnection.wabaId?.trim();

  if (!wabaId) {
    throw new Error("wabaId é obrigatório para salvar Endpoint Meta.");
  }

  let phoneContacts = [] as ReturnType<typeof pickPhoneContacts>;
  let primaryPhoneValue: string | undefined;
  const selectedPrimaryPhoneNumberId = metaConnection.primaryPhoneNumberId?.trim() ?? null;
  try {
    const phoneNumbersResponse = await fetchWabaPhoneNumbers(wabaId);
    phoneContacts = prioritizePhoneContacts(
      pickPhoneContacts(phoneNumbersResponse),
      selectedPrimaryPhoneNumberId,
    );
    primaryPhoneValue =
      phoneContacts[0]?.displayPhoneNumber ??
      pickPrimaryDisplayPhoneNumber(phoneNumbersResponse) ??
      undefined;
  } catch {
    phoneContacts = [];
    primaryPhoneValue = undefined;
  }

  const manualPrimaryPhoneValue = metaConnection.primaryPhoneValue?.trim();
  const resolvedPrimaryPhoneValue = primaryPhoneValue ?? manualPrimaryPhoneValue ?? undefined;

  await endpointService.createOrUpdateMetaEndpoint({
    organizationId: context?.organizationId,
    metaConnection,
    primaryPhoneValue: resolvedPrimaryPhoneValue,
    phoneContacts,
  });
}

async function persistFluxmedOption(
  context?: CrmSetupConfigurationContext,
): Promise<void> {
  const organizationId = context?.organizationId?.trim();
  if (!organizationId) return;

  const alreadyHasContract =
    await crmContractService.hasActiveCrmContractByOrganization({
      organizationId,
    });
  if (alreadyHasContract) return;

  const authority = `Organization/${oids.cnpj}-${organizationId}`;

  const contract: fhir5.Contract = {
    resourceType: "Contract",
    status: "executed",
    identifier: [
      {
        system: "https://fluxmed.com.br/crm/contract-tier",
        value: "crm50",
      },
    ],
    authority: [{ reference: authority }],
    applies: {
      start: new Date().toISOString(),
    },
  };

  await fhirClients.admin.create<fhir5.Contract>("Contract", contract);
}

async function clearMetaConnection(
  context?: CrmSetupConfigurationContext,
): Promise<void> {
  await endpointService.deactivateEndpointByContext({
    organizationId: context?.organizationId,
  });
}

export const crmSetupConfigurationService = {
  getStatus,
  persistMetaEndpoint,
  persistFluxmedOption,
  clearMetaConnection,
};
